import * as React from 'react'
import { useState } from 'react'
import { addMonths, subMonths, format } from 'date-fns'
import ChevronLeft from '../../assets/icons_new/ChevronLeftBlack.svg'
import ChevronRight from '../../assets/icons_new/ChevronRightBlack.svg'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { monthKeys } from '@/modules/shared/constants/months'
import { DateCalendar } from './DateCalendar'

type DatePickerSingleProps = {
  value?: string | null
  submit: (fullDate: string) => void
  setPreview?: (fullDate: string) => void
  confirmSelection?: boolean
}
const parseDateString = (value: string): Date => {
  const [yearStr, monthStr, dayStr] = value.trim().split('-')
  const day = parseInt(dayStr, 10)
  const month = parseInt(monthStr, 10) - 1
  const year = parseInt(yearStr, 10)
  return new Date(year, month, day)
}
const DateAndTimePickerPopup: React.FC<DatePickerSingleProps> = ({
  value,
  submit,
  setPreview,
  confirmSelection = false,
}) => {
  const { t } = useLocalize()

  const initialDate = value ? parseDateString(value) : null
  const [currentDate, setCurrentDate] = useState<Date>(
    initialDate ?? new Date(),
  )
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate)
  const [selectedTime, setSelectedTime] = useState<string>(
    value?.match(/^\d{4}-\d{2}-\d{2}[T\s]+(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/)?.[1] ??
      '00:00',
  )

  const generateTimeSlots = () => {
    const slots: string[] = []
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = hour.toString().padStart(2, '0')
        const m = minute.toString().padStart(2, '0')
        slots.push(`${h}:${m}`)
      }
    }
    return slots
  }

  const timeSlots = generateTimeSlots()

  const handleDayClick = (day: Date) => {
    setSelectedDate(day)

    const formatted = format(day, 'YYYY-MM-DD')

    if (!confirmSelection) {
      submit(formatted)
      return
    }

    if (setPreview) setPreview(formatted)
  }

  const handlePreviousMonth = () => setCurrentDate(subMonths(currentDate, 1))
  const handleNextMonth = () => setCurrentDate(addMonths(currentDate, 1))

  const handleSubmit = () => {
    if (!selectedDate) return
    const dateStr = format(selectedDate, 'YYYY-MM-DD')
    const fullDateTime = `${dateStr} ${selectedTime}`
    submit(fullDateTime)
  }

  return (
    <div className="tw-w-[500px] tw-select-none tw-rounded-xl tw-bg-white tw-font-sans tw-shadow">
      <div className="tw-flex">
        <div className="tw-flex tw-justify-between tw-space-x-4 tw-p-2 tw-pb-3">
          <div className="tw-flex-1">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-4">
              <img
                src={ChevronLeft}
                alt="Chevron Left"
                onClick={handlePreviousMonth}
                style={{ cursor: 'pointer' }}
                className="tw-w-5 hover:tw-opacity-90"
              />
              <p className="tw-font-semibold">
                {t(`${monthKeys[currentDate.getMonth()]}`)}{' '}
                {currentDate.getFullYear()}
              </p>
              <img
                src={ChevronRight}
                alt="Chevron Right"
                onClick={handleNextMonth}
                style={{ cursor: 'pointer' }}
                className="tw-w-5 hover:tw-opacity-90"
              />
            </div>

            <DateCalendar
              currentMonth={currentDate}
              startDate={selectedDate}
              type="singleDate"
              handleDayClick={handleDayClick}
            />
          </div>
        </div>

        <div className="tw-scrollbar-thin tw-border-b-1 tw-border-t-1 tw-border-base-400 tw-max-h-[332px] tw-w-[160px] tw-overflow-y-auto tw-border-l-2 tw-border-base-300">
          {timeSlots.map((time) => (
            <button
              key={time}
              onClick={() => setSelectedTime(time)}
              className={`tw-w-full tw-border-b tw-border-gray-100 tw-bg-[#FAFBFF] tw-px-4 tw-py-2 tw-text-center tw-text-sm tw-transition-colors hover:tw-bg-primary/10 ${
                selectedTime === time
                  ? 'tw-font-semibold tw-text-primary'
                  : 'tw-text-content-base'
              }`}
            >
              {time}
            </button>
          ))}
        </div>
      </div>

      {confirmSelection && (
        <div className="tw-flex tw-justify-end tw-border-t tw-border-base-300 tw-p-3">
          <button
            onClick={handleSubmit}
            disabled={!selectedDate}
            className={`tw-rounded-lg tw-p-3 tw-text-sm tw-font-medium hover:tw-opacity-90 ${
              selectedDate
                ? 'tw-bg-primary tw-text-white'
                : 'tw-bg-content-base/20 tw-text-gray-400'
            }`}
          >
            Zatwierdź
          </button>
        </div>
      )}
    </div>
  )
}

export default DateAndTimePickerPopup
